import { AlertDTO, MonitorDTO } from '@meshwatch/types';
import { CreateTableOutput } from 'aws-sdk/clients/dynamodb';
import { AWSError } from 'aws-sdk/lib/error';
import { PromiseResult } from 'aws-sdk/lib/request';
import { pickBy } from 'lodash';

import {
  DEFAULT_DYNAMO_DB_CLIENT,
  DynamoDBClient,
  DynamoDBTableClient,
} from '../../shared/aws/dynamodb';
import DateUtils from '../../util/date';
import { AlertDynamoItem, AlertSortKey } from '../alerts/types';
import { MonitorDynamoItem, MonitorSortKey } from '../monitors/types';

import { MONITORING_TABLE_SCHEMA } from './schema';
import { MonitoringHashKey } from './types';

abstract class DynamoMonitoringDatasource {
  protected readonly dynamoClient: DynamoDBTableClient;
  protected readonly dynamoRawClient: DynamoDBClient;
  protected readonly tableName: string;

  public constructor(dynamoClient: DynamoDBClient = DEFAULT_DYNAMO_DB_CLIENT) {
    this.dynamoClient = new DynamoDBTableClient(MONITORING_TABLE_SCHEMA.TableName, dynamoClient);
    this.dynamoRawClient = dynamoClient;
    this.tableName = MONITORING_TABLE_SCHEMA.TableName;
  }

  public createTable = (): Promise<PromiseResult<CreateTableOutput, AWSError>> => {
    return this.dynamoClient.createTable(MONITORING_TABLE_SCHEMA);
  };

  /* hashKey */
  public static encodeHashKey({ userId }: MonitoringHashKey): string {
    return `user#${userId}`;
  }

  public static decodeHashKey(hashKey: string): MonitoringHashKey {
    const s = hashKey.split('#');
    return { userId: s[1] };
  }

  /* alert */
  private decodeAlertSortKey(sortKey: string): AlertSortKey {
    const s = sortKey.split('#');
    return { monitorId: s[1], alertId: s[2] };
  }

  protected alertFromDynamoItem = (Item: AlertDynamoItem): AlertDTO => {
    const { userId } = DynamoMonitoringDatasource.decodeHashKey(Item.hashKey);
    const { monitorId, alertId } = this.decodeAlertSortKey(Item.sortKey);

    return {
      userId,
      id: alertId,
      monitorId,
      created: DateUtils.dateFromUnixTimestamp(Item.created),
      type: Item.type,
      scheduler: Item.scheduler,
      action: Item.action,
      actionValue: Item.actionValue,
      operation: Item.operation,
      value: Item.value,
      windowDuration: Item.windowDuration,
    };
  };

  /* monitor */
  private decodeMonitorSortKey(sortKey: string): MonitorSortKey {
    const s = sortKey.split('#');
    return { monitorId: s[1] };
  }

  protected monitorFromDynamoItem = (Item: MonitorDynamoItem): MonitorDTO => {
    const { userId } = DynamoMonitoringDatasource.decodeHashKey(Item.hashKey);
    const { monitorId } = this.decodeMonitorSortKey(Item.sortKey);

    const monitor: MonitorDTO = {
      userId,
      id: monitorId,
      created: DateUtils.dateFromUnixTimestamp(Item.created),
      name: Item.name,
      scheduler: Item.scheduler,
      apdex: Item.apdex,
      type: Item.type,
      headers: Item.headers,
      body: Item.body,
      endpoint: Item.endpoint,
      isBookmarked: Item.isBookmarked,
      location: Item.location,
      regions: Item.regions,
    };

    // Strip undefined fields to reduce ammount of network transfer
    return pickBy(monitor, value => value !== undefined) as MonitorDTO;
  };
}

export default DynamoMonitoringDatasource;
